import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, applyActionCode } from 'firebase/auth';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';

/**
 * Tela branded de confirmação de e-mail (handler customizado do Firebase).
 *
 * O e-mail de boas-vindas aponta o botão "Confirmar meu e-mail" para
 * /verificar-email?oobCode=XXX (em vez da página genérica do Firebase em
 * __/auth/action). Aqui aplicamos o código via applyActionCode e mostramos
 * o resultado com a identidade visual da IAT.
 */
const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const ranRef = useRef(false);

  useEffect(() => {
    // Evita rodar duas vezes (StrictMode) — o oobCode é de uso único.
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(location.search);

    // Modo preview (?preview=success|error|verifying) — só pra visualizar o layout.
    const preview = params.get('preview');
    if (preview) {
      setStatus(preview);
      return;
    }

    const oobCode = params.get('oobCode') || params.get('code');

    if (!oobCode) {
      setStatus('error');
      return;
    }

    const auth = getAuth();
    applyActionCode(auth, oobCode)
      .then(async () => {
        // Atualiza o usuário logado para refletir emailVerified=true
        try {
          if (auth.currentUser) await auth.currentUser.reload();
        } catch (_) {}
        setStatus('success');
      })
      .catch((err) => {
        console.error('[EmailVerification] applyActionCode falhou:', err);
        setStatus('error');
      });
  }, [location.search]);

  const goToApp = () => {
    const auth = getAuth();
    navigate(auth.currentUser ? '/chat' : '/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="mx-auto mb-4 animate-spin text-[#FFC107]" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Confirmando seu e-mail…</h1>
            <p className="text-gray-600">Só um instante.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto mb-4 text-green-500" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">E-mail confirmado!</h1>
            <p className="text-gray-600 mb-6">
              Tudo certo. Seu e-mail foi verificado e sua conta está liberada.
            </p>
            <button
              onClick={goToApp}
              className="bg-[#FFC107] hover:bg-[#1A1A20] text-white py-2 px-6 rounded-lg font-semibold transition-colors"
            >
              Ir para a plataforma
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Não foi possível confirmar</h1>
            <p className="text-gray-600 mb-6">
              O link pode ter expirado ou já ter sido usado. Faça login na plataforma e,
              se necessário, reenvie o e-mail de confirmação pelo aviso no topo da tela.
            </p>
            <button
              onClick={goToApp}
              className="bg-[#FFC107] hover:bg-[#1A1A20] text-white py-2 px-6 rounded-lg font-semibold transition-colors"
            >
              Ir para a plataforma
            </button>
          </>
        )}
        <p className="mt-6 text-sm text-gray-500">
          Dúvidas? <a href="mailto:contato@iatconsulting.com.br" className="text-[#0E0E12] hover:underline">contato@iatconsulting.com.br</a>
        </p>
      </div>
    </div>
  );
};

export default EmailVerification;
